import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Instagram, Linkedin, Youtube } from "lucide-react";
import logoCalriz from "../../assets/logos/logo-oficial-calriz.png";
import brasaoUniversidade from "../../assets/redesign/universidade/brasao-universidade.webp";
import NavItemLink from "../Header/NavItemLink";
import { NavItem, useNavLinks } from "../Header/useNavLinks";
import { useSpecialties } from "../../pages/Servicos/ServicesContent";
import { officeMapLink, useOffices } from "../../pages/Mapa/offices";
import "./Footer.css";

const SOCIAL_ICONS: Record<string, typeof Instagram> = {
  LinkedIn: Linkedin,
  YouTube: Youtube,
  Instagram,
};

const CONTACTS = [
  { label: "contato@calriz.com", href: "mailto:contato@calriz.com" },
  { label: "(11) 2394-7253", city: "São Paulo", href: "tel:+551123947253" },
  { label: "(304) 470-0005", city: "Orlando", href: "tel:+13044700005" },
];

export default function Footer() {
  const { t } = useTranslation();
  const { main, utility, social } = useNavLinks();
  const specialties = useSpecialties();
  const offices = useOffices();
  // landing da Universidade Calriz: brasão no lugar da logo, como no header dela
  const isUniversidade = useLocation().pathname.replace(/\/$/, "") === "/universidade-calriz";
  const [about, , , cases, blog, workWithUs, support] = main;
  const [whistleblower, intranet] = utility;

  const columns: { title: string; items: NavItem[] }[] = [
    {
      title: t("footer.solutions_title", "Soluções"),
      items: [
        ...specialties.map(({ title, to }) => ({ label: title, to })),
        { label: t("footer.catalog_link", "Catálogo de soluções"), to: "/solucoes" },
      ],
    },
    {
      title: t("footer.institutional_title", "Institucional"),
      items: [about, { ...cases, label: t("footer.cases_link", "Cases") }, blog, workWithUs],
    },
    {
      title: t("footer.support_title", "Suporte"),
      items: [support, whistleblower, intranet],
    },
  ];

  return (
    // A classe "footer" é mantida: as páginas de evento usam `.gd-page + .footer` / `.ia-page + .footer`.
    <footer className="footer cz-footer cz-scope">
      <div className="cz-footer__inner">
        <div className="cz-footer__grid">
          <div className="cz-footer__brand">
            {isUniversidade ? (
              <img src={brasaoUniversidade} alt="Universidade Calriz" className="cz-footer__logo cz-footer__logo--crest" />
            ) : (
              <img src={logoCalriz} alt="Calriz" className="cz-footer__logo" />
            )}
            <p>{t("footer.tagline", "Tecnologia para pessoas, educação e um futuro melhor.")}</p>
            <div className="cz-footer__social">
              {social.map((item) => {
                const Icon = SOCIAL_ICONS[item.label];
                return (
                  <a
                    key={item.label}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={item.label}
                    className="cz-footer__social-link"
                  >
                    {Icon ? <Icon size={16} aria-hidden="true" /> : item.label}
                  </a>
                );
              })}
            </div>
          </div>

          {columns.map((col) => (
            <nav key={col.title} className="cz-footer__col" aria-label={col.title}>
              <span className="cz-footer__title">{col.title}</span>
              {col.items.map((item) => (
                <NavItemLink key={item.label} item={item} className="cz-footer__link" />
              ))}
            </nav>
          ))}

          <div className="cz-footer__col">
            <span className="cz-footer__title">{t("footer.contact_title", "Fale com a gente")}</span>
            {CONTACTS.map((c) => (
              <a key={c.href} href={c.href} className="cz-footer__link">
                {c.label}
                {c.city && <span className="cz-footer__city"> · {c.city}</span>}
              </a>
            ))}
            <Link to="/#contato" className="cz-footer__link">{t("header.contact", "Fale Conosco")}</Link>
          </div>
        </div>

        <div className="cz-footer__offices">
          <span className="cz-footer__title">{t("offices.title", "Escritórios")}</span>
          {offices.map((office, i) => (
            <address key={office.key} className="cz-footer__office">
              <span className="cz-footer__office-city">
                <span className="cz-shape cz-shape--drop cz-footer__office-drop" aria-hidden="true" />
                {office.city}
                <span className="cz-footer__city"> · {office.tag}</span>
              </span>
              <span>{office.street}</span>
              <span>{office.postal}</span>
              <Link to={officeMapLink(i)} className="cz-footer__link cz-footer__office-map">
                {t("offices.see_on_map", "Ver no mapa")}
                <ArrowRight size={14} aria-hidden="true" />
              </Link>
            </address>
          ))}
        </div>

        <div className="cz-footer__bottom">
          <span>
            © {new Date().getFullYear()} Calriz. {t("footer.rights", "Todos os direitos reservados.")}
          </span>
          <nav className="cz-footer__legal" aria-label={t("footer.legal_title", "Documentos legais")}>
            <Link to="/politica-de-privacidade">{t("footer.privacy_short", "Privacidade")}</Link>
            <Link to="/termos-de-uso">{t("footer.terms_short", "Termos de uso")}</Link>
            <Link to="/lgpd">LGPD</Link>
          </nav>
        </div>
      </div>
    </footer>
  );
}
